import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth, type LoginResult } from '../../api/resources.js';
import { session } from '../../api/client.js';
import { Button } from '../../components/ui/button.js';
import { Field, Input } from '../../components/ui/input.js';
import { useAction } from '../../lib/hooks.js';
import { AuthError, AuthLayout } from './layout.js';
import { CodeInput } from './code-input.js';

/** Login (PRD §4, §69). Com segundo fator, a senha certa abre um segundo passo. */
export function LoginPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState('');

  const finish = (result: LoginResult) => {
    if (!result.token) return;
    session.set(result.token, result.organizations?.[0]?.id);
    navigate(result.emailVerified === false ? '/verify-email' : '/', { replace: true });
  };

  const submit = useAction(async () => {
    const result = await auth.login(form.email, form.password);
    if (result.mfaRequired && result.challenge) {
      setChallenge(result.challenge);
      return;
    }
    finish(result);
  });

  const second = useAction(async () => {
    if (!challenge) return;
    finish(await auth.loginMfa(challenge, code));
  });

  if (challenge) {
    return (
      <AuthLayout
        title="Segundo fator"
        description="Digite o código de 6 dígitos do seu aplicativo autenticador."
        footer={
          <button type="button" className="link" onClick={() => setChallenge(null)}>
            Voltar
          </button>
        }
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void second.run();
          }}
        >
          <AuthError error={second.error} />
          <CodeInput value={code} onChange={setCode} autoFocus />
          <Button
            className="mt-4 w-full"
            type="submit"
            disabled={second.pending || code.length < 6}
          >
            {second.pending ? 'Verificando…' : 'Entrar'}
          </Button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Entrar"
      description="Acesse o console da sua organização."
      footer={
        <>
          Não tem conta? <Link to="/signup">Criar conta grátis</Link>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit.run();
        }}
      >
        <AuthError error={submit.error} />
        <Field label="E-mail" htmlFor="email">
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={(event) => setForm((c) => ({ ...c, email: event.target.value }))}
          />
        </Field>
        <Field label="Senha" htmlFor="password">
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={form.password}
            onChange={(event) => setForm((c) => ({ ...c, password: event.target.value }))}
          />
        </Field>
        <div className="-mt-1 mb-4 text-right text-sm">
          <Link to="/forgot">Esqueci minha senha</Link>
        </div>
        <Button className="w-full" type="submit" disabled={submit.pending}>
          {submit.pending ? 'Entrando…' : 'Entrar'}
        </Button>
      </form>
    </AuthLayout>
  );
}
